import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useRef, useState } from "react";

import { type ModelProfileResponse } from "@/shared/domain/agentThread/AgentThreadRecord";

import styles from "./ModelProfileSelector.module.css";

/**
 * Where the popover sits relative to the trigger. Follows MUI's
 * ``top-end`` / ``top-start`` axis: ``end`` aligns the popover's right
 * edge with the trigger's right edge (extending left), ``start`` aligns
 * the left edges (extending right).
 *
 * ``top-end`` is the AgentThread-input default because the trigger lives at
 * the bottom-right of a textarea and extending left keeps the popover
 * inside the textarea's visual rectangle. Pages where the trigger sits
 * on the left side of the layout (e.g. the AgentEditor's "Setup"
 * section) pass ``top-start`` so the popover extends rightward into the
 * page rather than under the navigation sidebar.
 */
export type ModelProfileSelectorPlacement = "top-end" | "top-start";

interface ModelProfileSelectorProps {
  /** Available model profiles fetched from the API. */
  profiles: ModelProfileResponse[];
  /** The currently selected profile ID. */
  value: string;
  /** Callback invoked when the user picks a different profile. Not called when disabled. */
  onChange: (profileId: string) => void;
  /** When true, the trigger renders as read-only (no chevron, no popover). Used for existing threads. */
  disabled?: boolean;
  /** Popover anchor direction; defaults to ``top-end`` (the AgentThread-input layout). */
  placement?: ModelProfileSelectorPlacement;
}

export const ModelProfileSelector = ({
  profiles,
  value,
  onChange,
  disabled = false,
  placement = "top-end",
}: ModelProfileSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const selectedProfile = profiles.find((p) => p.id === value);
  const displayLabel = selectedProfile?.label ?? value;

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (profiles.length === 0) {
    return null;
  }

  // Single profile — show label but no selector
  if (profiles.length === 1) {
    return (
      <div className={styles.anchor}>
        <span className={`${styles.trigger} ${styles.disabled}`}>
          {profiles[0].label}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <button
        type="button"
        className={`${styles.trigger} ${disabled ? styles.disabled : ""}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => !prev);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Model: ${displayLabel}`}
      >
        {displayLabel}
        {!disabled && (
          <ExpandMoreIcon
            className={`${styles.chevron} ${isOpen ? styles.open : ""}`}
          />
        )}
      </button>

      {isOpen && (
        <div
          className={styles.popover}
          data-placement={placement}
          role="listbox"
        >
          {profiles.map((profile) => {
            const isSelected = profile.id === value;
            return (
              <div
                key={profile.id}
                role="option"
                aria-selected={isSelected}
                className={`${styles.option} ${isSelected ? styles.selected : ""}`}
                onClick={() => {
                  onChange(profile.id);
                  setIsOpen(false);
                }}
              >
                <div className={styles.optionHeader}>
                  <span className={styles.optionName}>{profile.label}</span>
                  {isSelected && <CheckIcon className={styles.checkIcon} />}
                </div>
                <span className={styles.optionDescription}>
                  {profile.description}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
